import L from 'leaflet';
import { useEffect, useRef } from 'react';

const PRISHTINA: [number, number] = [42.6629, 21.1655];

/**
 * Map for placing a venue. Click to set the point; the circle shows the clock-in geofence.
 * Uses OpenStreetMap tiles.
 */
export function VenueMap({ lat, lng, radius, onPick }: { lat: number | null; lng: number | null; radius: number; onPick: (lat: number, lng: number) => void }) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const circle = useRef<L.Circle | null>(null);
  const dot = useRef<L.CircleMarker | null>(null);
  const pickRef = useRef(onPick);
  pickRef.current = onPick;

  useEffect(() => {
    if (!el.current || map.current) return;
    const m = L.map(el.current, { center: lat !== null && lng !== null ? [lat, lng] : PRISHTINA, zoom: lat !== null ? 16 : 12 });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(m);
    m.on('click', (e: L.LeafletMouseEvent) => pickRef.current(+e.latlng.lat.toFixed(6), +e.latlng.lng.toFixed(6)));
    map.current = m;
    // The map sits in a modal that may still be animating its size.
    setTimeout(() => m.invalidateSize(), 50);
    return () => {
      m.remove();
      map.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const m = map.current;
    if (!m) return;
    circle.current?.remove();
    dot.current?.remove();
    if (lat === null || lng === null) return;
    circle.current = L.circle([lat, lng], { radius, color: '#1d7a46', weight: 2, dashArray: '6 5', fillOpacity: 0.12 }).addTo(m);
    dot.current = L.circleMarker([lat, lng], { radius: 7, color: '#fff', weight: 2.5, fillColor: '#ED1C24', fillOpacity: 1 }).addTo(m);
    if (!m.getBounds().contains([lat, lng])) m.setView([lat, lng], Math.max(m.getZoom(), 15));
  }, [lat, lng, radius]);

  return <div ref={el} className="map" />;
}

/** Read "42.66, 21.16" or a Google Maps / OpenStreetMap link into coordinates. */
export function parseCoordinates(text: string): { lat: number; lng: number } | null {
  const patterns = [/@(-?\d+\.\d+),(-?\d+\.\d+)/, /[?&](?:q|query|ll)=(-?\d+\.\d+),\s*(-?\d+\.\d+)/, /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/, /mlat=(-?\d+\.\d+)&mlon=(-?\d+\.\d+)/, /^\s*(-?\d+\.\d+)\s*[,;\s]\s*(-?\d+\.\d+)\s*$/];
  for (const p of patterns) {
    const m = p.exec(text);
    if (m) {
      const lat = Number(m[1]);
      const lng = Number(m[2]);
      if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
    }
  }
  return null;
}
